import { z } from 'zod'

/**
 * Service packageConfig discriminated union (amendment 2026-07-09, LOCKED).
 *
 * Six kinds, one per catalog family:
 *  - full_wrap        Coloured Wrap: coverage + material tier
 *  - commercial_print Commercial Graphics / Car Skin: coverage only
 *  - commercial_decal Commercial Decal Package: light / bold
 *  - ppf              clear Paint Protection Film: coverage + film tier
 *  - coloured_ppf     Coloured PPF: coverage only (its own tier set)
 *  - custom_quote     staff-quoted off-app (e.g. tint)
 *
 * Economy gate: Economy is never offered at Quarter coverage (a quarter wrap is
 * already an accent-priced job with nothing to value-engineer). Enforced here as a
 * cross-field rule so a bad config is rejected at the edge, and also hidden in the
 * picker.
 */
const kindSchemas = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('full_wrap'),
    materialTier: z.enum(['economy', 'standard', 'specialty', 'carbon']),
    coverageTier: z.enum(['quarter', 'half', 'three_quarter', 'full']),
  }),
  z.object({
    kind: z.literal('commercial_print'),
    coverageTier: z.enum(['quarter', 'half', 'three_quarter', 'full']),
  }),
  z.object({ kind: z.literal('commercial_decal'), package: z.enum(['light', 'bold']) }),
  z.object({
    kind: z.literal('ppf'),
    coverage: z.enum(['essentials', 'standard', 'full_front', 'full_wrap']),
    filmTier: z.enum(['ultimate', 'platinum', 'satin_matte']),
  }),
  z.object({
    kind: z.literal('coloured_ppf'),
    coverage: z.enum(['accent', 'partial_front', 'full_front', 'full_body']),
  }),
  z.object({ kind: z.literal('custom_quote') }),
])

export const packageConfigSchema = kindSchemas.superRefine((cfg, ctx) => {
  if (cfg.kind === 'full_wrap' && cfg.materialTier === 'economy' && cfg.coverageTier === 'quarter') {
    ctx.addIssue({
      code: 'custom',
      message: 'Economy is not offered at Quarter coverage',
      path: ['materialTier'],
    })
  }
})

export type ServicePackageConfig = z.infer<typeof packageConfigSchema>

// Never throws: returns null for anything that fails validation, including
// malformed or missing input (e.g. the Json? column being null). safeParse
// reports validation failures but still propagates exceptions thrown while
// reading the input, so the call is wrapped to make the contract total.
export function parsePackageConfig(json: unknown): ServicePackageConfig | null {
  try {
    const result = packageConfigSchema.safeParse(json)
    return result.success ? result.data : null
  } catch {
    return null
  }
}
